/**
 * TagInput — chip-style tag entry with Enter/comma-to-submit.
 *
 * Used for `competitors` on the TenantProfile. Trimmed on commit;
 * duplicates (case-insensitive) rejected silently.
 *
 * Phase 44.4.
 */

import { useCallback, useState } from "react";

export default function TagInput({
  label,
  tags = [],
  onChange,
  placeholder = "Type and press Enter",
  maxLength = 200,
  helpText,
}) {
  const [draft, setDraft] = useState("");

  const commitDraft = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft("");
      return;
    }
    const lower = trimmed.toLowerCase();
    if (tags.some((t) => (t || "").toLowerCase() === lower)) {
      setDraft("");
      return;
    }
    onChange([...tags, trimmed]);
    setDraft("");
  }, [draft, tags, onChange]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        commitDraft();
      } else if (e.key === "Backspace" && draft === "" && tags.length > 0) {
        // Quality-of-life: backspace on empty input removes the last tag.
        onChange(tags.slice(0, -1));
      }
    },
    [commitDraft, draft, tags, onChange]
  );

  const remove = useCallback(
    (index) => {
      const next = tags.slice();
      next.splice(index, 1);
      onChange(next);
    },
    [tags, onChange]
  );

  return (
    <div className="tag-input">
      {label && <label className="tag-input__label">{label}</label>}
      {helpText && <p className="tag-input__help">{helpText}</p>}
      <div className="tag-input__box">
        {tags.map((tag, i) => (
          <span key={`${tag}-${i}`} className="tag-input__chip">
            {tag}
            <button
              type="button"
              className="tag-input__chip-remove"
              onClick={() => remove(i)}
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          className="tag-input__field"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitDraft}
          placeholder={tags.length === 0 ? placeholder : ""}
          maxLength={maxLength}
        />
      </div>
    </div>
  );
}
